import * as React from "react";
import { useCallback, useState } from "react";
import { DEFAULT_PARSER_CONFIG } from "../constants/defaultParserConfig";
import { filterXmlFiles } from "../services/fileImportService";
import { runXmlFolderToColumns, runXmlToColumns } from "../services/parseWorkflow";

export const INPUT_MODES = {
  selection: "selection",
  folder: "folder",
};

export function useXmlToColumns({ onParseComplete } = {}) {
  const [mode, setMode] = useState(INPUT_MODES.selection);
  const [config, setConfig] = useState(DEFAULT_PARSER_CONFIG);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [pickError, setPickError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [folderFiles, setFolderFiles] = useState(null);
  const [folderFileCount, setFolderFileCount] = useState(0);

  const updateConfig = useCallback((key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  }, []);

  const changeMode = useCallback((nextMode) => {
    setMode(nextMode);
    setError(null);
    setPickError(null);
    setResult(null);
    setProgress(null);
    setFolderFiles(null);
    setFolderFileCount(0);
  }, []);

  const selectFolder = useCallback((fileList) => {
    const selectedCount = Array.from(fileList || []).length;
    const xmlFiles = filterXmlFiles(fileList);

    if (xmlFiles.length === 0) {
      setFolderFiles(null);
      setFolderFileCount(0);
      setPickError(
        selectedCount === 0
          ? "No files were selected."
          : `No .xml files found (${selectedCount} file(s) selected). Pick .xml files and try again.`
      );
      setError(null);
      setResult(null);
      return;
    }

    setFolderFiles(xmlFiles);
    setFolderFileCount(xmlFiles.length);
    setPickError(null);
    setError(null);
    setResult(null);
  }, []);

  const clearFolder = useCallback(() => {
    setFolderFiles(null);
    setFolderFileCount(0);
    setPickError(null);
  }, []);

  const runJob = useCallback(
    async (job) => {
      setIsRunning(true);
      setError(null);
      setPickError(null);
      setResult(null);
      setProgress({ phase: "starting", message: "Starting..." });

      try {
        const summary = await job();
        setResult(summary);
        setProgress(null);
        onParseComplete?.();
      } catch (err) {
        setError(err?.message || "Parsing failed.");
        setProgress(null);
      } finally {
        setIsRunning(false);
      }
    },
    [onParseComplete]
  );

  const parseSelection = useCallback(async () => {
    await runJob(() => runXmlToColumns(config, setProgress));
  }, [config, runJob]);

  const parseFolder = useCallback(async () => {
    if (!folderFiles || folderFiles.length === 0) {
      setPickError("Choose XML files or a folder first, then click Parse folder.");
      return;
    }

    await runJob(() => runXmlFolderToColumns(config, folderFiles, setProgress));
  }, [config, folderFiles, runJob]);

  return {
    mode,
    setMode: changeMode,
    config,
    updateConfig,
    progress,
    result,
    error,
    pickError,
    isRunning,
    folderFileCount,
    selectFolder,
    clearFolder,
    parseSelection,
    parseFolder,
  };
}
